import {
	AnimationBuilder,
	type AnimationPlayer,
	animate,
	style,
} from "@angular/animations";
import { isPlatformBrowser } from "@angular/common";
import {
	type AfterViewInit,
	Directive,
	ElementRef,
	InjectionToken,
	Injector,
	type OnDestroy,
	PLATFORM_ID,
	WritableSignal,
	inject,
	input,
	runInInjectionContext,
	signal,
} from "@angular/core";
import { nanoid } from "nanoid";

export const ANIMATIONS_ENABLED = new InjectionToken<WritableSignal<boolean>>(
	"ANIMATIONS_ENABLED",
	{
		providedIn: "root",
		factory: () => signal<boolean>(true),
	},
);

@Directive({
	selector: "[animate]",
	standalone: true,
})
export class AnimateDirective implements AfterViewInit, OnDestroy {
	public readonly delay = input<number>(0);
	public readonly threshold = input<number>(0.25);
	public readonly onScroll = input<string | boolean>(false);
	public readonly staggerChildren = input<
		string | boolean | { delay?: number; limit?: number }
	>(false);

	private readonly _animationDuration = 500;
	private readonly _observerOptions: IntersectionObserverInit = {
		root: null,
		rootMargin: "0px",
		threshold: this.threshold(),
	};

	private readonly _elementRef: ElementRef = inject(ElementRef);
	private readonly _platformId = inject(PLATFORM_ID);
	private readonly _injector = inject(Injector);
	private readonly _isBrowser: boolean = isPlatformBrowser(this._platformId);
	private readonly _animationBuilder: AnimationBuilder =
		inject(AnimationBuilder);

	private _animationPlayers: { [key: string]: AnimationPlayer | null } = {};
	private _intersectionObservers: {
		[key: string]: IntersectionObserver | null;
	} = {};

	private get _staggerDelay(): number {
		const staggerChildren = this.staggerChildren();
		if (staggerChildren !== false) {
			const delay =
				typeof staggerChildren === "object" ? staggerChildren.delay : 100;
			return delay || 100;
		}
		return this.delay();
	}

	public async ngAfterViewInit(): Promise<void> {
		const animationsEnabled = runInInjectionContext(this._injector, () =>
			inject(ANIMATIONS_ENABLED),
		);
		if (animationsEnabled() !== true) return;
		const staggerChildren = this.staggerChildren();
		if (staggerChildren !== false) {
			let limit = -1;
			if (typeof staggerChildren === "object") {
				limit = staggerChildren.limit ?? -1;
			}
			const children: Element[] = Array.from(
				this._elementRef.nativeElement.children,
			);
			if (limit > 0) {
				children.splice(limit);
			}
			await Promise.all(
				Array.from(children).map((child, i) => {
					const delay = this._staggerDelay * (i + 1);
					return this._init(child, delay);
				}),
			);
		} else {
			this._init(this._elementRef.nativeElement, this.delay());
		}
	}

	public ngOnDestroy(): void {
		for (const observer of Object.values(this._intersectionObservers)) {
			observer?.disconnect();
		}
		this._intersectionObservers = {};
		for (const player of Object.values(this._animationPlayers)) {
			player?.destroy();
		}
		this._animationPlayers = {};
	}

	private async _init(element: Element, delay: number): Promise<void> {
		if (!this._isBrowser) return;
		const animationId = nanoid(4);
		if (this.onScroll() !== false) {
			const isIntersecting = await this._isIntersecting(element);
			const adjustedDelay = isIntersecting === true ? delay : 100;
			this._initAnimationPlayer(element, animationId, adjustedDelay);
			this._initIntersectionObserver(element, animationId);
		} else {
			this._initAnimationPlayer(element, animationId, delay);
			this._onIntersection(animationId);
		}
	}

	private _isIntersecting(element: Element): Promise<boolean> {
		return new Promise((resolve) => {
			const observer = new IntersectionObserver((entries, observer) => {
				observer.disconnect();
				resolve(entries.some((entry) => entry.isIntersecting));
			}, this._observerOptions);
			observer.observe(element);
		});
	}

	private _initIntersectionObserver(element: Element, id: string): void {
		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) this._onIntersection(id);
			}
		}, this._observerOptions);
		observer.observe(element);
		this._intersectionObservers[id] = observer;
	}

	private _initAnimationPlayer(
		element: Element,
		id: string,
		delay: number,
	): void {
		const animation = this._animationBuilder.build([
			style({ opacity: 0, transform: "translateY(10px)" }),
			animate(
				`${this._animationDuration}ms ${delay}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
				style({ opacity: 1, transform: "translateY(0)" }),
			),
		]);
		const player = animation.create(element);
		this._animationPlayers[id] = player;
	}

	private _onIntersection(id: string): void {
		const player = this._animationPlayers[id];
		const observer = this._intersectionObservers[id];
		observer?.disconnect();
		player?.play();
	}
}
